/**
 * Booking Hostname Detection — Issue #2838
 *
 * `booking.ripia.jp` (公開予約サイト専用サブドメイン) からのアクセスかどうかを
 * 判定する。この判定は AppRouter 冒頭で行い、booking ホストでは認証フロー
 * (`AuthenticatedRoute`) を一切通さずに、`/:slug` 直マウントの公開予約サイトのみ
 * を公開する。`/booking/:slug` 系レガシー URL は `app.ripia.jp` 側で互換維持する。
 *
 * これにより `https://booking.ripia.jp/` (slug なし直打ち) が
 * `AuthenticatedRoute` → Clerk 初期化 → 認証ループに巻き込まれる問題を回避する。
 *
 * @see Issue #2838
 * @see Issue #2852 — `buildBookingPath` で host 分岐を吸収
 */

import type { BookingNestedRoutePath } from '../routes/bookingRoutes';

/**
 * `BOOKING_NESTED_ROUTE_PATHS` から index route (空文字) を除外した、
 * 実ステップ名だけのユニオン。`buildBookingPath` の `step` 引数に使い、
 * 「index は省略で指定」という API 契約を型に載せる。
 */
export type BookingStepPath = Exclude<BookingNestedRoutePath, ''>;

/**
 * 本番 booking サブドメインのデフォルト hostname。
 *
 * この値は Cloudflare/Vercel に登録された公開予約サイト用サブドメインと
 * 一致している必要がある。変更時は DNS レコード・Vercel の domain 設定・
 * Sentry allow-list 等と同時に更新すること。
 *
 * Staging/preview 環境では `VITE_BOOKING_HOSTNAME` 環境変数で上書き可能。
 */
export const DEFAULT_BOOKING_HOSTNAME = 'booking.ripia.jp';

/**
 * hostname 比較は大文字小文字を区別しない (RFC 1035 §2.3.3: hostname は
 * case-insensitive)。末尾空白や全体の casing ずれによる false negative を防ぐ。
 */
function normalizeHostname(hostname: string): string {
  return hostname.trim().toLowerCase();
}

/**
 * booking ドメインの hostname を取得する。
 * `VITE_BOOKING_HOSTNAME` 環境変数が設定されていればそれを優先する。
 *
 * `length > 0` ガードは staging で env を空文字のまま残したミスを
 * `DEFAULT_BOOKING_HOSTNAME` へフェイルセーフで戻すための防御。空文字を
 * そのまま採用すると `isBookingHost()` が **全ホストで常に false** を返し、
 * staging の環境変数設定漏れが routing バグとしてサイレントに顕在化するのを防ぐ。
 */
export function getBookingHostname(): string {
  const configured = import.meta.env.VITE_BOOKING_HOSTNAME?.trim();
  return configured && configured.length > 0
    ? configured
    : DEFAULT_BOOKING_HOSTNAME;
}

/**
 * 現在のブラウザ hostname が booking ドメインかどうかを判定する。
 *
 * @param hostname デフォルトは `window.location.hostname`。テスト/SSR 用に
 *   差し替え可能。SSR 環境 (`window` が undefined) では常に false を返す。
 */
export function isBookingHost(
  hostname: string = typeof window !== 'undefined'
    ? window.location.hostname
    : ''
): boolean {
  if (!hostname) return false;
  return (
    normalizeHostname(hostname) === normalizeHostname(getBookingHostname())
  );
}

/**
 * 予約フローのナビゲーション用 URL を host 別に生成する。Issue #2852 の
 * バグ (`navigate('/booking/<slug>/<step>')` 直書きが booking.ripia.jp で 404 に
 * なる) を再発させないためのヘルパー。
 *
 * - booking.ripia.jp では `/:slug[/<step>]` (`/:slug` 直マウント)
 * - 通常ホスト (app.ripia.jp / localhost) では `/booking/:slug[/<step>]` (互換ルート)
 *
 * `step` の型は `BookingStepPath` (= `BookingNestedRoutePath` から index route を
 * 除外したもの) に制約されており、存在しないステップを渡すとコンパイルエラーに
 * なる (SSOT は `routes/bookingRoutes.ts`)。index route には `step` 省略で到達する。
 *
 * 絶対パス helper 方式を採用した理由: React Router v6 の相対ナビゲーション
 * `navigate('menu')` は親ルートの位置に依存するため、useParams/useLocation の
 * 状態や nested route の深さに応じて挙動が変わる。絶対パスを 1 箇所で生成する
 * 方がテスト可能性・デバッグ時の認知負荷・ミスに対する安全性で勝る。
 *
 * @param slug 予約サイトの店舗 slug
 * @param step ネストされた子ルート。省略時はホーム (index route)
 */
export function buildBookingPath(slug: string, step?: BookingStepPath): string {
  return buildBookingPathWithHost(
    slug,
    step,
    typeof window !== 'undefined' ? window.location.hostname : ''
  );
}

/**
 * @internal テスト / リファクタリング補助。プロダクションコードから直接呼ぶことは
 * 想定しておらず、通常は `buildBookingPath` を使うこと。
 *
 * `hostname` を明示注入することで、`window.location` をモックせずに純関数
 * として host 別 URL 生成ロジックを検証できる下位 API。
 * `no-restricted-imports` ESLint ルールでプロダクションコードからの import を
 * 禁止し、`@internal` の意図を静的解析で強制する。
 */
export function buildBookingPathWithHost(
  slug: string,
  step: BookingStepPath | undefined,
  hostname: string
): string {
  const base = isBookingHost(hostname) ? `/${slug}` : `/booking/${slug}`;
  return step ? `${base}/${step}` : base;
}
